/**
 * @file ForgotPasswordPage.tsx
 * @description Public page: user enters email, server sends a reset link
 *   via Resend if the email exists. UI always shows the same generic
 *   "we sent it if it exists" message — never reveals whether the email
 *   is registered.
 * @module Pages
 * @security Response from the API is always 204, regardless of whether
 *   the email exists. UX-side this means we always show success.
 */

import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthLayout } from '../components/common/AuthLayout';
import { Button } from '../components/common/Button';
import { FormField } from '../components/common/FormField';
import { requestPasswordReset } from '../lib/auth-api';

const Schema = z.object({ email: z.string().email() });

function ForgotPasswordForm(): JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const parsed = Schema.safeParse({ email });
    if (!parsed.success) {
      setEmailError(t('login.email_invalid'));
      return;
    }
    setEmailError(undefined);
    setLoading(true);
    try {
      await requestPasswordReset(parsed.data.email);
    } finally {
      // Always claim success; server returns 204 even for unknown emails.
      setDone(true);
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="w-full max-w-sm text-center space-y-3">
        <h1 className="text-xl font-semibold text-slate-900">{t('forgot.sent_title')}</h1>
        <p className="text-sm text-slate-500">{t('forgot.sent_body')}</p>
        <Link to="/login" className="text-sm text-brand-strong hover:underline">
          {t('forgot.back_to_login')}
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="w-full max-w-sm space-y-4" noValidate>
      <h1 className="text-2xl font-bold text-slate-900">{t('forgot.title')}</h1>
      <p className="text-sm text-slate-500">{t('forgot.subtitle')}</p>
      <FormField
        id="email"
        label={t('login.email_label')}
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={emailError}
        required
      />
      <Button type="submit" loading={loading} className="w-full">
        {t('forgot.submit')}
      </Button>
      <p className="text-sm text-center">
        <Link to="/login" className="text-brand-strong hover:underline">
          {t('forgot.back_to_login')}
        </Link>
      </p>
    </form>
  );
}

export default function ForgotPasswordPage(): JSX.Element {
  return (
    <AuthLayout>
      <ForgotPasswordForm />
    </AuthLayout>
  );
}
